import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Mail, Lock, User, Building2, 
  AlertCircle, ArrowLeft, ArrowRight,
  Terminal, ShieldCheck, Zap, Rocket, CheckCircle2
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';
import { adminBuiltInVariantsApi, API_BASE_URL, authApi } from '../services/api';

export const Register: React.FC = () => {
  const [step, setStep] = useState<'register' | 'verify'>('register');
  const [verificationCode, setVerificationCode] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    companyName: '',
    companyEmail: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);  
  const [searchParams] = useSearchParams();
  const templateParam = searchParams.get('template');

  const { register, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [selectedVariant, setSelectedVariant] = useState<any>(null);

  useEffect(() => {
    const loadVariant = async () => {
      if (!templateParam) return;
      
      try {
        const variant = await adminBuiltInVariantsApi.getById(templateParam);
        setSelectedVariant(variant);
      } catch (err) {
        console.warn("Template não encontrado:", templateParam);
      }
    };

    loadVariant();
  }, [templateParam]);

  // Regex para nomes (aceita acentos, ç, ã, õ, espaços, hífen, apóstrofo)
  const nameRegex = /^[\p{Letter}\s'’-]{2,60}$/u;

  const validateField = (name: string, value: string) => {
    let error = '';

    switch (name) {
      case 'firstName':
        if (!value.trim()) error = 'O nome próprio é obrigatório';
        else if (!nameRegex.test(value.trim())) error = 'Nome com caracteres inválidos';
        else if (value.trim().length > 60) error = 'Nome muito longo (máx. 60 caracteres)';
        break;

      case 'lastName':
        if (!value.trim()) error = 'O apelido é obrigatório';
        else if (!nameRegex.test(value.trim())) error = 'Apelido com caracteres inválidos';
        else if (value.trim().length > 60) error = 'Apelido muito longo (máx. 60 caracteres)';
        break;

      case 'email':
        if (!value.trim()) error = 'O e-mail é obrigatório';
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) error = 'E-mail inválido';
        break;

      case 'companyName':
        if (!value.trim()) error = 'O nome da empresa é obrigatório';
        else if (value.trim().length < 2) error = 'Nome da empresa muito curto';
        else if (value.trim().length > 100) error = 'Nome da empresa muito longo';
        break;

      case 'companyEmail':
        if (value.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          error = 'E-mail da empresa inválido';
        }
        break;

      case 'password':
        if (!value) error = 'A palavra-passe é obrigatória';
        else if (value.length < 8) error = 'Mínimo 8 caracteres';
        else if (value.length > 128) error = 'Muito longa (máx. 128)';
        break;

      case 'confirmPassword':
        if (!value) error = 'Confirme a palavra-passe';
        else if (value !== formData.password) error = 'As palavras-passe não coincidem';
        break;

      default:
        break;
    }

    return error;
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    Object.keys(formData).forEach((key) => {
      const err = validateField(key, formData[key as keyof typeof formData]);
      if (err) newErrors[key] = err;
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Verifica disponibilidade da empresa (nome ou email) em tempo real
  const checkCompanyAvailability = async (field: 'name' | 'email', value: string) => {
    try {
      const res = await fetch(`${API_BASE_URL}/admin/companies/check`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ [field]: value }),
      });

      const data = await res.json();

      if (!res.ok) {
        setErrors(prev => ({
          ...prev,
          [field === 'name' ? 'companyName' : 'companyEmail']: data.message || 'Já existe'
        }));
      } else {
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[field === 'name' ? 'companyName' : 'companyEmail'];
          return newErrors;
        });
      }
    } catch (err) {
      console.error('Erro ao verificar disponibilidade:', err);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setServerError(null);

    if (touched[name]) {
      const error = validateField(name, value);
      setErrors(prev => ({ ...prev, [name]: error }));
    }

    if (name === 'companyName' && value.trim().length >= 3) {
      checkCompanyAvailability('name', value.trim());
    }

    if (name === 'companyEmail' && value.trim().length > 5) {
      checkCompanyAvailability('email', value.trim().toLowerCase());
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    const error = validateField(name, value);
    setErrors(prev => ({ ...prev, [name]: error }));
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setServerError(null);

    const allTouched = Object.keys(formData).reduce((acc, key) => ({ ...acc, [key]: true }), {});
    setTouched(allTouched);

    if (!validateForm()) {
      toast.error('Por favor corrija os erros indicados');
      return;
    }

    setLoading(true);
    try {
      const response = await register({
        email: formData.email.trim(),
        password: formData.password,
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        companyName: formData.companyName.trim(),
        companyEmail: formData.companyEmail.trim() || undefined,
        variant: templateParam || 'default'
      });

      if (response.accessToken) {
        localStorage.setItem('accessToken', response.accessToken);
      }
      if (response.refreshToken) {
        localStorage.setItem('refreshToken', response.refreshToken);
      }

      toast.success('Conta criada! Enviámos um código de verificação para o seu email.');
      setStep('verify');

    } catch (err: any) {
      const msg = err.response?.data?.message || 'Erro ao criar conta.';
      setServerError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (verificationCode.length !== 6) {
      toast.error('Insira o código completo de 6 dígitos');
      return;
    }

    setLoading(true);
    try {
      const verifyResponse = await authApi.verifyEmail(
        formData.email.trim(), 
        verificationCode.trim()
      );

      toast.success('E-mail verificado com sucesso!');

      if (verifyResponse.accessToken) {
        localStorage.setItem('accessToken', verifyResponse.accessToken);
        if (verifyResponse.refreshToken) {
          localStorage.setItem('refreshToken', verifyResponse.refreshToken);
        }
        try {
          await refreshUser();
        } catch (_) { /* ignora */ }
      }

      const needsCheckout = selectedVariant?.price && selectedVariant.price > 0;

      if (needsCheckout && templateParam) {
        navigate(`/template-checkout?variant=${templateParam}`);
      } else {
        navigate('/company/edit?setup=1');
      }
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Código inválido ou expirado');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-[#0B0C1E] text-[#e2e8f0] font-sans antialiased selection:bg-[#52C47C] selection:text-[#0B0C1E] flex flex-col lg:flex-row overflow-x-hidden relative">
      
      {/* Structural Grid Lines */}
      <div className="fixed inset-0 pointer-events-none z-0 flex justify-between px-4 sm:px-12 opacity-[0.03]">
        <div className="w-[1px] h-full bg-white" />
        <div className="w-[1px] h-full bg-white" />
        <div className="w-[1px] h-full bg-white" />
        <div className="w-[1px] h-full bg-white" />
      </div>

      {/* Glow futurista */}
      <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-[#52C47C]/5 blur-[160px] pointer-events-none" />

      {/* Coluna Esquerda: Branding & Informação Institucional (Desktop) */}
      <div className="hidden lg:flex lg:w-5/12 relative flex-col justify-between p-12 xl:p-16 border-r border-white/10 z-10">
        
        <div>
          <Link to="/" className="inline-flex items-center gap-3 group">
            <img 
              src="https://meupontodevenda.com/cdn/logo-white.svg" 
              alt="MPV — Meu Ponto de Venda" 
              className="h-7 w-auto object-contain transition-transform group-hover:scale-95"
            />
            <span className="font-mono text-[10px] tracking-widest text-[#8087A8] border-l border-white/10 pl-3">
              SISTEMA v4.0
            </span>
          </Link>
        </div>

        <div className="max-w-md my-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 border border-[#52C47C]/30 bg-[#52C47C]/10 font-mono text-[11px] text-[#52C47C] tracking-widest uppercase mb-6">
            <Terminal className="w-3.5 h-3.5" />
            <span>REGISTO DE EMPRESA</span>
          </div>

          <motion.h2 
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl font-light text-white leading-[1.15] mb-6 tracking-tight"
          >
            Inicialize a sua operação com <span className="font-normal italic text-[#52C47C]">estrutura e escala.</span>
          </motion.h2>

          <p className="text-slate-300 text-sm font-light leading-relaxed mb-8">
            Crie a conta da sua empresa no MPV e tenha acesso imediato ao ponto de venda, gestão de stock, faturação comercial e vitrine digital integrada.
          </p>

          <div className="space-y-4 font-mono">
            {[
              {
                code: "SETUP-01",
                title: "Ativação Imediata",
                desc: "Configuração do ambiente de trabalho em menos de 3 minutos."
              },
              {
                code: "POS-02",
                title: "Vendas & Caixa",
                desc: "Emissão de documentos comerciais e pagamentos móveis M-Pesa."
              },
              {
                code: "WEB-03",
                title: "Loja Online 24/7",
                desc: "Catálogo virtual sincronizado diretamente com o seu inventário."
              }
            ].map((item, idx) => (
              <motion.div 
                key={idx}
                initial={{ opacity: 0, x: -15 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 + idx * 0.1 }}
                className="p-4 bg-[#12142B] border border-white/10 flex items-start gap-4"
              >
                <span className="text-[10px] text-[#52C47C] border border-[#52C47C]/30 px-2 py-0.5 mt-0.5 shrink-0">
                  {item.code}
                </span>
                <div>
                  <h4 className="text-white text-xs font-bold uppercase tracking-wider">{item.title}</h4>
                  <p className="text-slate-400 text-xs font-light font-sans mt-0.5 leading-relaxed">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        <div className="pt-8 border-t border-white/10 font-mono text-[10px] text-slate-500 tracking-wider flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-[#52C47C]" />
            <span>MOÇAMBIQUE & PALOP • EDIÇÃO BUSINESS</span>
          </div>
          <span>© {new Date().getFullYear()} MPV</span>
        </div>

      </div>

      {/* Coluna Direita: Formulário */}
      <div className="flex-1 flex flex-col justify-between p-6 sm:p-12 relative z-10 min-h-screen overflow-y-auto">
        
        {/* Mobile Header Logo */}
        <div className="lg:hidden flex items-center justify-between pb-6 border-b border-white/10 mb-6">
          <Link to="/" className="flex items-center gap-2">
            <img 
              src="https://meupontodevenda.com/cdn/logo-white.svg" 
              alt="MPV — Meu Ponto de Venda" 
              className="h-6 w-auto object-contain"
            />
          </Link>
          <span className="font-mono text-[10px] text-[#52C47C] border border-[#52C47C]/30 px-2 py-0.5 uppercase">
            v4.0
          </span>
        </div>

        <div className="my-auto flex items-center justify-center py-6">
          <AnimatePresence mode="wait">
            {step === 'register' ? (
              <motion.div 
                key="register"
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.98 }}
                className="w-full max-w-xl"
              >
                {/* Card Container */}
                <div className="bg-[#12142B] border border-white/10 p-8 sm:p-10 shadow-2xl relative">
                  
                  <div className="mb-8 text-left">
                    <span className="font-mono text-[10px] text-[#52C47C] tracking-widest uppercase block mb-2">
                      // REGISTO DE NOVA EMPRESA
                    </span>
                    <h1 className="text-3xl font-light text-white tracking-tight mb-2">
                      Criar Conta de Empresa
                    </h1>
                    <p className="text-slate-400 text-xs font-light">
                      Preencha os dados da sua empresa para inicializar o seu ambiente comercial.
                    </p>
                  </div>

                  {serverError && (
                    <motion.div 
                      initial={{ opacity: 0, y: -8 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-red-500/10 border border-red-500/30 p-4 mb-6 flex items-start gap-3 text-red-300 font-mono text-xs"
                    >
                      <AlertCircle className="h-4 w-4 text-red-400 shrink-0 mt-0.5" />
                      <p className="flex-1">{serverError}</p>
                    </motion.div>
                  )}

                  <form onSubmit={handleRegister} className="space-y-5">
                    
                    {/* Nome e Apelido */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block font-mono text-xs text-slate-400 uppercase tracking-wider mb-2">
                          Nome Próprio
                        </label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                            <User className="h-4 w-4 text-slate-500" />
                          </div>
                          <input
                            name="firstName"
                            placeholder="Ex: João"
                            value={formData.firstName}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            className={`block w-full pl-10 pr-4 py-3 bg-[#0B0C1E] border text-white placeholder-slate-600 focus:border-[#52C47C] focus:ring-1 focus:ring-[#52C47C]/30 transition-all outline-none font-sans text-sm ${
                              errors.firstName && touched.firstName ? 'border-red-500/60' : 'border-white/10'
                            }`}
                          />
                        </div>
                        {touched.firstName && errors.firstName && (
                          <p className="text-red-400 font-mono text-[11px] mt-1">{errors.firstName}</p>
                        )}
                      </div>

                      <div>
                        <label className="block font-mono text-xs text-slate-400 uppercase tracking-wider mb-2">
                          Apelido
                        </label>
                        <input
                          name="lastName"
                          placeholder="Ex: Silva"
                          value={formData.lastName}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          className={`block w-full px-4 py-3 bg-[#0B0C1E] border text-white placeholder-slate-600 focus:border-[#52C47C] focus:ring-1 focus:ring-[#52C47C]/30 transition-all outline-none font-sans text-sm ${
                            errors.lastName && touched.lastName ? 'border-red-500/60' : 'border-white/10'
                          }`}
                        />
                        {touched.lastName && errors.lastName && (
                          <p className="text-red-400 font-mono text-[11px] mt-1">{errors.lastName}</p>
                        )}
                      </div>
                    </div>

                    {/* Email de Acesso */}
                    <div>
                      <label className="block font-mono text-xs text-slate-400 uppercase tracking-wider mb-2">
                        E-mail do Administrador
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                          <Mail className="h-4 w-4 text-slate-500" />
                        </div>
                        <input
                          name="email"
                          type="email"
                          placeholder="admin@empresa.co.mz"
                          value={formData.email}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          className={`block w-full pl-10 pr-4 py-3 bg-[#0B0C1E] border text-white placeholder-slate-600 focus:border-[#52C47C] focus:ring-1 focus:ring-[#52C47C]/30 transition-all outline-none font-sans text-sm ${
                            errors.email && touched.email ? 'border-red-500/60' : 'border-white/10'
                          }`}
                        />
                      </div>
                      {touched.email && errors.email && (
                        <p className="text-red-400 font-mono text-[11px] mt-1">{errors.email}</p>
                      )}
                    </div>

                    {/* Bloco Empresa */}
                    <div className="p-5 bg-[#0B0C1E] border border-white/10 space-y-4">
                      <span className="font-mono text-[10px] text-[#52C47C] uppercase tracking-widest block">
                        // DADOS DA EMPRESA
                      </span>
                      
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block font-mono text-xs text-slate-400 uppercase tracking-wider mb-2">
                            Nome Legal da Empresa
                          </label>
                          <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                              <Building2 className="h-4 w-4 text-slate-500" />
                            </div>
                            <input
                              name="companyName"
                              placeholder="Nome da sua empresa"
                              value={formData.companyName}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              className={`block w-full pl-10 pr-4 py-3 bg-[#12142B] border text-white placeholder-slate-600 focus:border-[#52C47C] focus:ring-1 focus:ring-[#52C47C]/30 transition-all outline-none font-sans text-sm ${
                                errors.companyName && touched.companyName ? 'border-red-500/60' : 'border-white/10'
                              }`}
                            />
                          </div>
                          {touched.companyName && errors.companyName && (
                            <p className="text-red-400 font-mono text-[11px] mt-1">{errors.companyName}</p>
                          )}
                        </div>

                        <div>
                          <label className="block font-mono text-xs text-slate-400 uppercase tracking-wider mb-2">
                            E-mail Geral (opcional)
                          </label>
                          <input
                            name="companyEmail"
                            type="email"
                            placeholder="contacto@empresa.co.mz"
                            value={formData.companyEmail}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            className={`block w-full px-4 py-3 bg-[#12142B] border text-white placeholder-slate-600 focus:border-[#52C47C] focus:ring-1 focus:ring-[#52C47C]/30 transition-all outline-none font-sans text-sm ${
                              errors.companyEmail && touched.companyEmail ? 'border-red-500/60' : 'border-white/10'
                            }`}
                          />
                          {touched.companyEmail && errors.companyEmail && (
                            <p className="text-red-400 font-mono text-[11px] mt-1">{errors.companyEmail}</p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Senhas */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block font-mono text-xs text-slate-400 uppercase tracking-wider mb-2">
                          Palavra-passe
                        </label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                            <Lock className="h-4 w-4 text-slate-500" />
                          </div>
                          <input
                            name="password"
                            type="password"
                            placeholder="Mín. 8 caracteres"
                            value={formData.password}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            className={`block w-full pl-10 pr-4 py-3 bg-[#0B0C1E] border text-white placeholder-slate-600 focus:border-[#52C47C] focus:ring-1 focus:ring-[#52C47C]/30 transition-all outline-none font-sans text-sm ${
                              (errors.password || errors.confirmPassword) && touched.password ? 'border-red-500/60' : 'border-white/10'
                            }`}
                          />
                        </div>
                        {touched.password && errors.password && (
                          <p className="text-red-400 font-mono text-[11px] mt-1">{errors.password}</p>
                        )}
                      </div>

                      <div>
                        <label className="block font-mono text-xs text-slate-400 uppercase tracking-wider mb-2">
                          Confirmar Palavra-passe
                        </label>
                        <input
                          name="confirmPassword"
                          type="password"
                          placeholder="Repita a palavra-passe"
                          value={formData.confirmPassword}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          className={`block w-full px-4 py-3 bg-[#0B0C1E] border text-white placeholder-slate-600 focus:border-[#52C47C] focus:ring-1 focus:ring-[#52C47C]/30 transition-all outline-none font-sans text-sm ${
                            errors.confirmPassword && touched.confirmPassword ? 'border-red-500/60' : 'border-white/10'
                          }`}
                        />
                        {touched.confirmPassword && errors.confirmPassword && (
                          <p className="text-red-400 font-mono text-[11px] mt-1">{errors.confirmPassword}</p>
                        )}
                      </div>
                    </div>

                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full py-3.5 px-4 bg-[#52C47C] hover:bg-white text-[#0B0C1E] font-mono font-bold text-xs uppercase tracking-widest transition-all flex items-center justify-center gap-2 group shadow-lg shadow-[#52C47C]/15 disabled:opacity-50 mt-6"
                    >
                      {loading ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-[#0B0C1E] border-t-transparent"></div>
                      ) : (
                        <>
                          <span>Finalizar Registo da Empresa</span>
                          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </>
                      )}
                    </button>
                  </form>

                  <div className="mt-8 pt-6 border-t border-white/10 text-center font-mono text-xs text-slate-400">
                    <p>
                      Já tem conta registada?{' '}
                      <Link to="/login" className="text-[#52C47C] font-bold hover:underline ml-1 uppercase">
                        Entrar no Sistema →
                      </Link>
                    </p>
                  </div>
                </div>
              </motion.div>
            ) : (
              /* PASSO DE VERIFICAÇÃO */
              <motion.div 
                key="verify"
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                className="w-full max-w-md"
              >
                <div className="bg-[#12142B] border border-white/10 p-8 sm:p-10 text-center shadow-2xl">
                  
                  <div className="w-16 h-16 bg-[#52C47C]/10 border border-[#52C47C]/30 flex items-center justify-center mx-auto mb-6 text-[#52C47C]">
                    <Mail size={32} />
                  </div>
                  
                  <span className="font-mono text-[10px] text-[#52C47C] tracking-widest uppercase block mb-2">
                    // CÓDIGO DE SEGURANÇA
                  </span>
                  
                  <h3 className="text-2xl font-light text-white mb-2 tracking-tight">
                    Verifique o seu E-mail
                  </h3>
                  
                  <p className="text-slate-400 text-xs font-light mb-8 leading-relaxed">
                    Introduza o código de 6 dígitos que enviámos para <br/>
                    <span className="text-white font-mono font-medium">{formData.email}</span>
                  </p>

                  <form onSubmit={handleVerify} className="space-y-6">
                    <input
                      type="text"
                      maxLength={6}
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value)}
                      placeholder="000000"
                      className="w-full text-center text-3xl tracking-[0.8rem] py-4 bg-[#0B0C1E] border border-white/10 text-[#52C47C] focus:border-[#52C47C] focus:ring-1 focus:ring-[#52C47C]/30 outline-none transition-all font-mono"
                      required
                      autoFocus
                    />
                    
                    <button
                      type="submit"
                      disabled={loading || verificationCode.length !== 6}
                      className="w-full py-3.5 bg-[#52C47C] hover:bg-white text-[#0B0C1E] font-mono font-bold text-xs uppercase tracking-widest transition-all disabled:opacity-50 shadow-lg shadow-[#52C47C]/15"
                    >
                      {loading ? "A validar código..." : "Confirmar e Ativar Conta"}
                    </button>
                  </form>

                  <button
                    onClick={() => setStep('register')}
                    className="mt-8 inline-flex items-center text-xs font-mono text-slate-500 hover:text-white transition-colors gap-2"
                  >
                    <ArrowLeft size={14} /> Voltar e corrigir dados
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Mobile Footer */}
        <div className="lg:hidden text-center font-mono text-[10px] text-slate-500 pt-6">
          © {new Date().getFullYear()} MPV — MEU PONTO DE VENDA
        </div>

      </div>

    </div>
  );
};

export default Register;